import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import {
  Security,
  Fingerprint,
  CloudUpload,
  VerifiedUser,
  AdminPanelSettings,
  Code,
  Storage,
  Api,
} from '@mui/icons-material';

const AboutPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          mb: 6,
          borderRadius: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            About DecentralizedKYC
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{ textAlign: 'center', opacity: 0.9, mb: 2 }}
          >
            Revolutionary Decentralized Identity Verification System
          </Typography>
          <Typography
            variant="body1"
            component="p"
            sx={{ textAlign: 'center', opacity: 0.8, maxWidth: '600px', mx: 'auto' }}
          >
            Empowering users with complete control over their identity data through blockchain technology and NFT-based verification
          </Typography>
        </Container>
      </Box>

        {/* Mission & Vision */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4" component="h2" gutterBottom color="primary">
                  Our Mission
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  To revolutionize identity verification by providing users with complete sovereignty over their identity data, 
                  enabling secure, privacy-preserving, and interoperable digital identity solutions through blockchain technology.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h4" component="h2" gutterBottom color="primary">
                  Our Vision
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  To become the global standard for decentralized identity verification, 
                  creating a world where users own and control their identity data while maintaining compliance and trust.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Core Features */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}
          >
            Core Features
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                icon: <Fingerprint sx={{ fontSize: 40, color: 'primary.main' }} />,
                title: 'NFT Identity Minting',
                description: 'One wallet, one identity. Free minting with complete ownership and control over your digital identity NFT.',
              },
              {
                icon: <VerifiedUser sx={{ fontSize: 40, color: 'primary.main' }} />,
                title: 'Multi-Template KYC',
                description: 'Flexible verification supporting personal, enterprise, and high-net-worth templates with layered authentication.',
              },
              {
                icon: <AdminPanelSettings sx={{ fontSize: 40, color: 'primary.main' }} />,
                title: 'Smart Permissions',
                description: 'Role-based access control with fine-grained permissions and whitelist mechanisms for secure operations.',
              },
              {
                icon: <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />,
                title: 'IPFS Storage',
                description: 'Decentralized storage with encryption ensuring privacy protection and data sovereignty.',
              },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Technology Stack */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}
          >
            Technical Architecture
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Code sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary">
                      Smart Contracts
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Solidity" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="ERC721" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="OpenZeppelin" size="small" sx={{ mr: 1, mb: 1 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Ethereum-based smart contracts implementing ERC721 NFT standard with role-based access control, 
                    whitelist management, and secure identity verification logic.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Storage sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary">
                      Decentralized Storage
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="IPFS" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Encryption" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Pinata" size="small" sx={{ mr: 1, mb: 1 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    IPFS-based distributed storage with encryption for sensitive identity data, 
                    ensuring privacy protection and permanent accessibility of verification documents.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Api sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary">
                      Frontend & API
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="React" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Web3.js" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip label="Material-UI" size="small" sx={{ mr: 1, mb: 1 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Modern Web3 application with React frontend, Web3 wallet integration, 
                    and RESTful APIs for seamless blockchain interaction and user experience.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Core Innovations */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}
          >
            Core Innovations
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Security sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary">
                      User Data Sovereignty
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Revolutionary approach where users maintain complete ownership and control over their identity data, 
                    breaking free from centralized platforms.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • NFT-based identity ownership<br />
                    • Encrypted IPFS storage<br />
                    • User-controlled data sharing
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VerifiedUser sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary">
                      Interoperable Verification
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    One verification enables access across multiple platforms and services, 
                    eliminating redundant KYC processes and improving user experience.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Cross-platform compatibility<br />
                    • Standardized verification protocols<br />
                    • API integration for third parties
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Market Value & Business Model */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}
          >
            Market Value & Business Model
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom color="primary">
                    Verification Services
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Revenue from KYC verification services for enterprises and high-value individual verifications
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom color="primary">
                    API Integration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    API call fees for third-party platforms integrating our decentralized identity verification
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom color="primary">
                    Premium Features
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Advanced features like batch operations, priority support, and enhanced security options
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            Join the DecentralizedKYC Ecosystem
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Start your decentralized identity journey today and experience true data sovereignty with blockchain-powered verification
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/assets"
              variant="contained"
              size="large"
            >
              Identity List
            </Button>
            <Button
              component={Link}
              to="/mint"
              variant="outlined"
              size="large"
            >
              Mint Identity NFT
            </Button>
            {/* <Button
              component={Link}
              to="/admin"
              variant="text"
              size="large"
            >
              Admin Panel
            </Button> */}
          </Box>
        </Box>
    </Container>
  );
};

export default AboutPage;